import {
  Activity,
  ArrowRight,
  Baby,
  BrainCircuit,
  CalendarCheck,
  FileText,
  FolderOpen,
  HeartPulse,
  Lock,
  Menu,
  Pill,
  Plane,
  ScanLine,
  Search,
  ShieldCheck,
  Sparkles,
  Stethoscope,
  Syringe,
  X,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";

import {
  AccountSecurityPage,
  AllergyEditorPage,
  AllergiesPage,
  AppointmentDetailPage,
  AppointmentEditPage,
  AppointmentNewPage,
  AppointmentsPage,
  ConditionEditorPage,
  DashboardPage,
  DocumentViewerPage,
  EmergencyAccessPage,
  EmergencyPage,
  HealthInsurancePage,
  HealthProfileExportPage,
  HealthRecordsPage,
  KnownHealthConditionsPage,
  MedicationDetailPage,
  MedicationEditPage,
  MedicationsHistoryPage,
  MedicationNewPage,
  MedicationsPage,
  MedicineOrderDetailPage,
  MedicineOrderNewPage,
  MedicineOrdersPage,
  MorePage,
  MyFilesPage,
  ProfileEditPage,
  ProfilePage,
  SettingsPage,
  UpcomingCarePage,
  VaccinationDetailPage,
  VaccinationEditPage,
  VaccinationNewPage,
  VaccinationsPage,
  WorkspaceShell,
} from "./app/Workspace";
import careImg from "./assets/care.jpg";
import { AuthProvider } from "./auth/AuthProvider";
import { useAuth } from "./auth/useAuth";
import heroAi from "./assets/hero-ai.jpg";
import { BrandedLoader } from "./components/BrandedLoader";
import securityImg from "./assets/security.jpg";
import { RuntimeConfigProvider } from "./config/RuntimeConfigContext";
import { parseRuntimeConfig, type RuntimeConfig } from "./config/runtime";
import { EmergencyCard } from "./emergencyAccess/EmergencyCard";
import {
  EmergencyCardNotFoundError,
  EmergencyCardUnavailableError,
  fetchDeviceEmergencyCard,
  getDeviceEmergencyAccessStatus,
  type EmergencyCard as EmergencyCardData,
} from "./emergencyAccess/client";
import { replaceLocation } from "./lib/browserNavigation";
import { CareRemindersDialog } from "./notifications/CareRemindersDialog";
import { NotificationEnablePrompt } from "./notifications/NotificationEnablePrompt";
import { AlreadyInstalledNotice } from "./pwa/AlreadyInstalledNotice";
import { InstallMedoPilotEntry } from "./pwa/InstallMedoPilotEntry";
import { IosInstallInstructions } from "./pwa/IosInstallInstructions";
import { PwaStatusPrompts } from "./pwa/PwaStatusPrompts";

const productSections = [
  { label: "Why MedoPilot", href: "/#why" },
  { label: "How it works", href: "/#how" },
  { label: "Privacy", href: "/privacy" },
  { label: "Terms", href: "/terms" },
];

const benefits: Array<{
  icon: LucideIcon;
  title: string;
  body: string;
  span: string;
}> = [
  {
    icon: FolderOpen,
    title: "One place for your health history",
    body: "Reports, prescriptions, diagnoses, allergies, vaccinations and hospital visits kept together instead of spread across folders, phones and inboxes.",
    span: "lg:col-span-3",
  },
  {
    icon: Search,
    title: "Find what matters in seconds",
    body: "Look back at an old blood test or last year's discharge summary without digging through photographs and email threads.",
    span: "lg:col-span-3",
  },
  {
    icon: CalendarCheck,
    title: "Follow your treatment with ease",
    body: "Keep treatment schedules, medicine durations and follow-ups visible, with clearer next steps and less guesswork.",
    span: "lg:col-span-2",
  },
  {
    icon: BrainCircuit,
    title: "Prepare with better context",
    body: "Review the right history before appointments, second opinions or family-care conversations, without relying on memory.",
    span: "lg:col-span-2",
  },
  {
    icon: Plane,
    title: "It travels with you",
    body: "Move between cities, countries or care routines with your own medical journey kept in one understandable place.",
    span: "lg:col-span-2",
  },
];

const timeline: Array<{ icon: LucideIcon; label: string; note: string }> = [
  { icon: Baby, label: "Childhood", note: "Vaccinations and early records" },
  { icon: Stethoscope, label: "Consultations", note: "Visits across clinics and cities" },
  { icon: FileText, label: "Reports", note: "Labs, scans, discharge summaries" },
  { icon: Pill, label: "Treatment today", note: "Current medicines and follow-ups" },
  { icon: HeartPulse, label: "Years ahead", note: "A record that keeps growing with you" },
];

const useCases = [
  "Records spread across clinics and devices",
  "Chronic or recurring treatment protocols",
  "Caring for children, parents or dependents",
  "Years of visits to different specialists",
  "Relocating cities or living between countries",
  "Preparing a second opinion elsewhere",
];

const trustPrinciples = [
  {
    title: "You stay in control",
    body: "Your record is organized around you, so the history you add stays understandable when life gets busy.",
  },
  {
    title: "Built for continuity",
    body: "A longitudinal timeline helps connect reports, prescriptions, visits and follow-ups into one ongoing care story.",
  },
  {
    title: "Designed for protection",
    body: "Health information needs careful security boundaries from the first day of the product.",
  },
  {
    title: "Responsible intelligence",
    body: "MedoPilot can organize, summarize and remind. It does not diagnose, prescribe or replace qualified healthcare professionals.",
  },
];

const callbackCompletions = new Map<string, Promise<string>>();

function completeCallbackOnce(callbackUrl: string, completeSignIn: () => Promise<string>) {
  const existing = callbackCompletions.get(callbackUrl);
  if (existing) {
    return existing;
  }

  const completion = completeSignIn();
  callbackCompletions.set(callbackUrl, completion);
  return completion;
}

function useRuntimeConfig() {
  const [config, setConfig] = useState<RuntimeConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (typeof fetch !== "function") {
      setConfig({
        accountService: null,
        healthRecordService: null,
        medicationService: null,
        documentService: null,
        notificationService: null,
        medicineOrderService: null,
        auth: null,
      });
      setLoading(false);
      return undefined;
    }

    let active = true;

    fetch("/config.json", { cache: "no-store" })
      .then((response) => (response.ok ? response.json() : null))
      .then((nextConfig: unknown) => parseRuntimeConfig(nextConfig as Parameters<typeof parseRuntimeConfig>[0]))
      .then((nextConfig) => {
        if (active) {
          setConfig(nextConfig);
          setError(null);
        }
      })
      .catch(() => {
        if (active) {
          setConfig(null);
          setError("MedoPilot configuration could not be loaded.");
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, []);

  return { config, error, loading };
}

function BrandLockup({ variant = "light" }: { variant?: "light" | "dark" }) {
  const dark = variant === "dark";

  return (
    <span className="flex min-w-0 items-center gap-3">
      <img
        src="/medopilot-icon.png"
        alt=""
        aria-hidden="true"
        className={`${dark ? "h-12 brightness-0 invert drop-shadow-[0_0_18px_rgba(255,255,255,0.12)] sm:h-[3.25rem]" : "h-11 sm:h-12"} w-auto shrink-0 transition-transform group-hover:scale-105`}
        width={52}
        height={52}
        loading={dark ? "lazy" : undefined}
      />
      <span className="flex min-w-0 flex-col items-start">
        <span className={`brand-wordmark ${dark ? "text-[1.45rem] text-white" : "text-[1.28rem] text-foreground sm:text-[1.55rem]"}`}>
          MedoPilot
        </span>
        <span className={`${dark ? "text-[0.72rem] text-white/66" : "text-[0.64rem] text-muted-foreground sm:text-[0.72rem]"} mt-1 block text-left font-semibold leading-none`}>
          Your Health Companion
        </span>
      </span>
    </span>
  );
}

function SiteHeader() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`sticky top-0 z-50 border-b border-primary/10 bg-white/90 backdrop-blur-md transition-shadow ${
        scrolled ? "shadow-soft" : ""
      }`}
    >
      <div className="mx-auto flex min-h-20 max-w-7xl items-center justify-between gap-3 px-4 py-3 sm:px-5">
        <a href="/" className="group flex min-w-0 items-center" aria-label="MedoPilot home">
          <BrandLockup />
        </a>

        <nav aria-label="Primary" className="hidden items-center gap-1 lg:flex">
          {productSections.map((item) => (
            <a
              key={item.label}
              href={item.href}
              className="rounded-full px-3.5 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
            >
              {item.label}
            </a>
          ))}
        </nav>

        <div className="hidden items-center gap-2 lg:flex">
          <InstallMedoPilotEntry className="rounded-full border border-primary/25 px-4 py-2.5 text-sm font-semibold text-primary transition-colors hover:bg-primary/5" />
          <a
            href="/app"
            className="rounded-full bg-[linear-gradient(135deg,#0a6fd8,#4f46e5)] px-5 py-2.5 text-sm font-semibold text-primary-foreground shadow-[0_14px_28px_-16px_rgba(11,111,216,0.8)] transition-transform hover:-translate-y-0.5"
          >
            Sign in
          </a>
        </div>

        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          aria-expanded={open}
          aria-controls="mobile-nav"
          aria-label={open ? "Close menu" : "Open menu"}
          className="inline-flex size-11 shrink-0 items-center justify-center rounded-full border border-border bg-card text-foreground lg:hidden"
        >
          {open ? <X className="size-5" /> : <Menu className="size-5" />}
        </button>
      </div>

      {open ? (
        <div id="mobile-nav" className="border-t border-border bg-background lg:hidden">
          <nav aria-label="Mobile" className="mx-auto flex max-w-7xl flex-col gap-1 px-4 py-4 sm:px-5">
            {productSections.map((item) => (
              <a
                key={item.label}
                href={item.href}
                onClick={() => setOpen(false)}
                className="rounded-xl px-4 py-3.5 text-base font-medium hover:bg-secondary"
              >
                {item.label}
              </a>
            ))}
            <div className="mt-3 flex flex-col gap-2">
              <a href="/app" className="rounded-full bg-primary px-5 py-3.5 text-center text-base font-semibold text-primary-foreground">
                Sign in
              </a>
              <InstallMedoPilotEntry
                className="rounded-full border border-primary/25 px-5 py-3.5 text-center text-base font-semibold text-primary"
                onClick={() => setOpen(false)}
              />
            </div>
          </nav>
        </div>
      ) : null}
    </header>
  );
}

function SiteFooter() {
  return (
    <footer className="bg-[#031b3d] text-white">
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-5 sm:py-12">
        <div className="grid gap-9 md:grid-cols-[minmax(0,360px)_auto_auto] md:items-start md:justify-start md:gap-x-20 md:gap-y-9 lg:gap-x-24">
          <div className="w-full max-w-[360px]">
            <a href="/" className="group inline-flex items-center" aria-label="MedoPilot home">
              <BrandLockup variant="dark" />
            </a>
            <p className="mt-5 max-w-[360px] text-sm leading-relaxed text-white/68">
              Your medical journey, organized for life. MedoPilot keeps your clinical history
              structured and ready when you need it.
            </p>
          </div>

          <nav aria-label="Footer product" className="min-w-[7rem] text-left md:pt-2">
              <h2 className="text-xs font-semibold uppercase tracking-widest text-white/55">Product</h2>
              <ul className="mt-4 space-y-3 text-left text-sm">
                <li>
                  <a href="/#why" className="hover:underline">
                    Why MedoPilot
                  </a>
                </li>
                <li>
                  <a href="/#how" className="hover:underline">
                    How it works
                  </a>
                </li>
                <li>
                  <a href="/app" className="hover:underline">
                    Sign in
                  </a>
                </li>
                <li>
                  <InstallMedoPilotEntry className="text-left hover:underline" />
                </li>
              </ul>
          </nav>

          <nav aria-label="Footer legal" className="min-w-[7rem] text-left md:pt-2">
              <h2 className="text-xs font-semibold uppercase tracking-widest text-white/55">Legal</h2>
              <ul className="mt-4 space-y-3 text-left text-sm">
                <li>
                  <a href="/privacy" className="hover:underline">
                    Privacy
                  </a>
                </li>
                <li>
                  <a href="/terms" className="hover:underline">
                    Terms
                  </a>
                </li>
              </ul>
          </nav>
        </div>

        <div className="mt-10 border-t border-white/10 pt-6">
          <p className="text-xs leading-relaxed text-white/62">
            MedoPilot helps you organize health records, follow treatment prescribed by your
            healthcare professional, and keep useful information within reach. It does not diagnose
            conditions, prescribe treatment, or replace professional medical judgement. Always
            consult a qualified healthcare professional about your health.
          </p>
          <p className="mt-4 text-xs text-white/62">
            &copy; {new Date().getFullYear()} MedoPilot. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}

function LandingPage() {
  return (
    <div className="min-h-screen">
      <SiteHeader />
      <main id="main">
        <section className="relative overflow-hidden bg-[linear-gradient(180deg,#eef6ff_0%,#f8fbff_46%,#f4f7fc_100%)]">
          <img
            src={heroAi}
            alt=""
            aria-hidden="true"
            className="pointer-events-none absolute -top-14 right-0 hidden w-[58%] opacity-[0.16] mix-blend-multiply [mask-image:radial-gradient(ellipse_58%_58%_at_70%_40%,black,transparent)] lg:block"
            width={1600}
            height={1200}
          />
          <div className="pointer-events-none absolute right-[-22rem] top-[-14rem] size-[34rem] rounded-full bg-[radial-gradient(circle,#6aa9ff_0%,rgba(106,169,255,0.34)_34%,transparent_70%)] opacity-70 blur-3xl sm:right-[-12rem] sm:size-[46rem]" />

          <div className="relative mx-auto grid max-w-7xl gap-10 px-4 py-10 sm:gap-12 sm:px-5 sm:py-20 lg:grid-cols-[1.02fr_0.98fr] lg:items-start lg:gap-14 lg:py-24">
            <div className="min-w-0 reveal" data-visible="true">
              <h1 className="max-w-[22rem] text-[clamp(2.35rem,12vw,4.15rem)] font-extrabold leading-[1.08] text-foreground sm:max-w-4xl sm:leading-[1.03]">
                Your medical journey,
                <span className="block text-gradient-brand">organized for life.</span>
              </h1>
              <p className="mt-5 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-xl">
                Labs, scans, prescriptions and diagnoses arrive from everywhere. MedoPilot brings
                them into one structured health record, ready to search and understand when
                care depends on it.
              </p>

              <div className="mt-9 grid w-full max-w-sm gap-3 sm:flex sm:max-w-none sm:flex-wrap">
                <a
                  href="/app"
                  className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-[linear-gradient(135deg,#075fc6,#3f35d2)] px-6 py-4 text-base font-semibold text-primary-foreground shadow-[0_24px_50px_-24px_rgba(7,95,198,0.95)] transition-all hover:-translate-y-0.5 hover:shadow-[0_30px_62px_-28px_rgba(7,95,198,0.92)] sm:w-auto sm:px-7"
                >
                  Start your health record
                  <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
                </a>
                <a
                  href="#how"
                  className="inline-flex w-full items-center justify-center rounded-full border border-primary/15 bg-white px-6 py-4 text-base font-semibold text-foreground shadow-soft transition-all hover:-translate-y-0.5 hover:bg-secondary hover:shadow-premium sm:w-auto sm:px-7"
                >
                  See how it works
                </a>
              </div>
              <div className="mt-8 flex flex-col items-start gap-3 text-sm text-muted-foreground sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-6">
                <span className="inline-flex items-center gap-2">
                  <Lock className="size-4 text-primary" aria-hidden="true" /> Private by design
                </span>
                <span className="inline-flex items-center gap-2">
                  <ScanLine className="size-4 text-primary" aria-hidden="true" /> Document capture
                </span>
                <span className="inline-flex items-center gap-2">
                  <Sparkles className="size-4 text-primary" aria-hidden="true" /> Structured timeline
                </span>
              </div>
            </div>

            <div className="min-w-0 reveal" data-visible="true">
              <div className="relative">
                <div className="absolute -inset-3 rounded-[2rem] bg-[radial-gradient(circle,#6aa9ff_0%,rgba(76,103,235,0.22)_48%,transparent_72%)] opacity-80 blur-2xl" aria-hidden="true" />
                <div className="card-glass relative min-w-0 rounded-[1.5rem] p-4 sm:p-7">
                  <div className="flex min-w-0 items-center justify-between gap-4">
                    <div className="min-w-0">
                      <p className="text-[0.7rem] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                        Health timeline
                      </p>
                      <p className="mt-1 font-display text-lg font-bold">A record that keeps up with you</p>
                    </div>
                    <img src="/medopilot-icon.png" alt="" className="size-10 shrink-0" width={40} height={40} />
                  </div>

                  <ol className="mt-6 space-y-2.5">
                    {timeline.map((item) => (
                      <li
                        key={item.label}
                        className="group flex min-w-0 items-start gap-3 rounded-2xl border border-primary/10 bg-white px-3.5 py-3.5 shadow-soft transition-all hover:-translate-y-0.5 hover:border-primary/35 hover:shadow-premium sm:items-center sm:gap-4 sm:px-4"
                      >
                        <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-[linear-gradient(135deg,#075fc6,#4438d1)] text-white shadow-[0_14px_28px_-18px_rgba(7,95,198,0.9)] transition-transform group-hover:-translate-y-0.5">
                          <item.icon className="size-5" aria-hidden="true" />
                        </span>
                        <span className="min-w-0">
                          <span className="block text-sm font-semibold">{item.label}</span>
                          <span className="block text-sm leading-snug text-muted-foreground">{item.note}</span>
                        </span>
                      </li>
                    ))}
                  </ol>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="border-y border-primary/10 bg-white">
          <div className="mx-auto grid max-w-7xl gap-6 px-4 py-8 sm:grid-cols-3 sm:px-5">
            {[
              { icon: ScanLine, title: "Capture records", body: "Bring reports and prescriptions into one place" },
              { icon: Activity, title: "Build the timeline", body: "Every episode kept in clinical order" },
              { icon: ShieldCheck, title: "Prepare with clarity", body: "The right context ready before care conversations" },
            ].map((item) => (
              <div key={item.title} className="flex min-w-0 items-start gap-3.5 sm:items-center">
                <span className="grid size-10 shrink-0 place-items-center rounded-xl icon-premium">
                  <item.icon className="size-5" aria-hidden="true" />
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-bold">{item.title}</span>
                  <span className="block text-sm text-muted-foreground">{item.body}</span>
                </span>
              </div>
            ))}
          </div>
        </section>

        <section id="why" className="relative scroll-mt-24 overflow-hidden bg-[linear-gradient(135deg,#061b43,#0d347c_54%,#061f4d)] py-20 text-white sm:py-28">
          <img
            src={securityImg}
            alt=""
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 size-full object-cover opacity-[0.12] [mask-image:linear-gradient(to_bottom,transparent,black_32%,black_68%,transparent)]"
            width={1408}
            height={1008}
            loading="lazy"
          />
          <div className="relative mx-auto max-w-7xl px-4 sm:px-5">
            <div className="max-w-2xl">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-200">The problem</p>
              <h2 className="mt-4 text-3xl font-bold sm:text-4xl">
                Fragmented records in. One continuous care story out.
              </h2>
              <p className="mt-5 text-lg leading-relaxed text-white/72">
                Most people cannot answer simple questions about their own health - which medicine
                was prescribed three years ago, when the last scan was done, or what a parent is
                allergic to. Not because they do not care, but because the information was never
                kept in one place.
              </p>
            </div>

            <div className="mt-12 grid gap-4 sm:grid-cols-2">
              <div className="min-w-0 rounded-2xl border border-white/12 bg-white/10 p-5 shadow-[0_30px_86px_-58px_rgba(0,0,0,0.75)] backdrop-blur transition-all hover:-translate-y-1 hover:bg-white/12 sm:p-7">
                <h3 className="text-xs font-semibold uppercase tracking-[0.18em] text-white/58">
                  How it usually looks
                </h3>
                <ul className="mt-5 space-y-3 text-base text-white/70">
                  <li>Reports in a drawer, a phone gallery and three email accounts</li>
                  <li>Prescriptions photographed and forgotten</li>
                  <li>Repeating your history from memory at every new clinic</li>
                  <li>Carrying a folder of papers to a second opinion abroad</li>
                </ul>
              </div>
              <div className="min-w-0 rounded-2xl border border-blue-200/30 bg-blue-300/12 p-5 shadow-[0_30px_86px_-58px_rgba(0,0,0,0.75)] backdrop-blur transition-all hover:-translate-y-1 hover:bg-blue-200/14 sm:p-7">
                <h3 className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-200">
                  With MedoPilot
                </h3>
                <ul className="mt-5 space-y-3 text-base">
                  <li>One unified record: history, treatment, documents and vitals</li>
                  <li>Structured entries that still make sense years later</li>
                  <li>A clearer view before appointments and care decisions</li>
                  <li>Portable history across cities, countries and changing routines</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section id="how" className="scroll-mt-24 bg-[linear-gradient(180deg,#f8fbff,#f5f7fb)] py-20 sm:py-28">
          <div className="mx-auto max-w-7xl px-4 sm:px-5">
            <div className="max-w-2xl">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">The platform</p>
              <h2 className="mt-4 text-3xl font-bold sm:text-4xl">
                Practical tools for a lifetime of care.
              </h2>
            </div>

            <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-6">
              {benefits.map((benefit) => (
                <article
                  key={benefit.title}
                  className={`group card-premium relative min-w-0 overflow-hidden rounded-2xl p-5 sm:p-7 ${benefit.span}`}
                >
                  <span className="relative grid size-11 place-items-center rounded-2xl icon-premium group-hover:-translate-y-0.5">
                    <benefit.icon className="size-5" aria-hidden="true" />
                  </span>
                  <h3 className="relative mt-5 text-lg font-bold">{benefit.title}</h3>
                  <p className="relative mt-3 text-[0.975rem] leading-relaxed text-muted-foreground">
                    {benefit.body}
                  </p>
                </article>
              ))}

              <article className="relative min-w-0 overflow-hidden rounded-2xl border border-primary/10 bg-[linear-gradient(135deg,#ffffff,#eef4ff_62%,#e9efff)] p-5 text-foreground shadow-lift transition-all hover:-translate-y-1 hover:shadow-[0_38px_82px_-50px_rgba(28,70,170,0.45)] sm:col-span-2 sm:p-7 lg:col-span-6">
                <div className="relative flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
                  <div className="max-w-2xl">
                    <span className="grid size-11 place-items-center rounded-2xl icon-premium">
                      <ShieldCheck className="size-5" aria-hidden="true" />
                    </span>
                    <h3 className="mt-5 text-xl font-bold">You stay at the centre</h3>
                    <p className="mt-3 leading-relaxed text-muted-foreground">
                      Your record belongs to you. MedoPilot keeps your history structured around
                      your own care journey and day-to-day follow-up.
                    </p>
                  </div>
                  <a
                    href="/app"
                    className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-[linear-gradient(135deg,#075fc6,#3f35d2)] px-6 py-3.5 text-sm font-semibold text-primary-foreground shadow-[0_20px_42px_-24px_rgba(7,95,198,0.9)] transition-all hover:-translate-y-0.5 hover:shadow-[0_26px_58px_-28px_rgba(7,95,198,0.95)] sm:w-auto md:self-start"
                  >
                    Get started
                    <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
                  </a>
                </div>
              </article>
            </div>
          </div>
        </section>

        <section className="bg-paper py-20 text-ink sm:py-28">
          <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-5 lg:grid-cols-[1fr_1fr] lg:items-center">
            <div className="relative min-w-0">
              <div className="absolute -inset-2 rounded-2xl bg-[radial-gradient(circle,#6aa9ff_0%,rgba(68,56,209,0.2)_48%,transparent_72%)] opacity-70 blur-2xl sm:-inset-4" aria-hidden="true" />
              <img
                src={careImg}
                alt="Abstract visualization of a health timeline with lab results, vitals and medication data"
                className="relative aspect-[4/3] w-full rounded-2xl object-cover shadow-lift transition-transform duration-500 hover:scale-[1.012]"
                width={1408}
                height={1104}
                loading="lazy"
              />
              <div className="card-glass absolute bottom-3 left-3 right-3 flex min-w-0 items-start gap-3 rounded-2xl px-3.5 py-3 sm:bottom-4 sm:left-4 sm:right-auto sm:items-center sm:px-4">
                <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-primary text-primary-foreground">
                  <Activity className="size-4" aria-hidden="true" />
                </span>
                <p className="min-w-0 text-sm font-semibold leading-snug text-foreground">Health context ready when you need it</p>
              </div>
            </div>
            <div className="min-w-0">
              <h2 className="text-3xl font-bold sm:text-4xl">Built for patients, families and caregivers.</h2>
              <p className="mt-5 text-lg leading-relaxed text-ink-muted">
                Chronic care, paediatric records, elderly parents or your own routine check-ups -
                MedoPilot is designed to make medical history easier to understand and carry
                forward.
              </p>
              <ul className="mt-8 grid gap-3 sm:grid-cols-2">
                {useCases.map((item) => (
                  <li
                    key={item}
                    className="min-w-0 rounded-2xl border border-paper-strong bg-white px-4 py-4 text-[0.975rem] font-medium shadow-soft transition-all hover:-translate-y-0.5 hover:border-primary/20 hover:shadow-premium sm:px-5"
                  >
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section className="bg-[linear-gradient(180deg,#f8fbff,#eef2f8)] py-20 sm:py-28">
          <div className="mx-auto max-w-7xl px-4 sm:px-5">
            <div className="grid min-w-0 gap-10 rounded-2xl border border-primary/10 bg-[linear-gradient(145deg,#ffffff,#f7fbff)] p-5 shadow-premium sm:p-12 lg:grid-cols-[0.85fr_1.15fr]">
              <div className="min-w-0">
                <span className="grid size-12 place-items-center rounded-2xl icon-premium">
                  <ShieldCheck className="size-6" aria-hidden="true" />
                </span>
                <h2 className="mt-6 text-[clamp(1.875rem,9vw,2.25rem)] font-bold leading-tight">Security belongs in the architecture.</h2>
                <p className="mt-4 leading-relaxed text-muted-foreground">
                  Nothing is more personal than a medical history. MedoPilot treats health data as
                  sensitive information that needs careful permissions, clear boundaries and
                  responsible handling.
                </p>
              </div>
              <dl className="grid min-w-0 gap-6 sm:grid-cols-2">
                {trustPrinciples.map((item) => (
                  <div key={item.title} className="min-w-0 border-l-2 border-primary/40 pl-4 transition-all hover:border-primary hover:pl-5">
                    <dt className="font-display text-base font-bold">{item.title}</dt>
                    <dd className="mt-2 text-[0.95rem] leading-relaxed text-muted-foreground">{item.body}</dd>
                  </div>
                ))}
              </dl>
            </div>

            <p className="mx-auto mt-10 max-w-3xl text-center text-sm leading-relaxed text-muted-foreground">
              <Syringe className="mr-2 inline size-4 align-[-2px]" aria-hidden="true" />
              MedoPilot is a health-record, organization and information-support
              platform. It does not diagnose conditions, prescribe medicines or replace the
              judgement of a qualified healthcare professional.
            </p>
          </div>
        </section>

        <section className="relative overflow-hidden cta-gradient-premium py-9 text-white sm:py-12 lg:py-14">
          <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-white/25" aria-hidden="true" />
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-[#031b3d]" aria-hidden="true" />
          <div className="pointer-events-none absolute left-[64%] top-1/2 size-[22rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#5c8dff]/24 opacity-85 blur-3xl sm:size-[34rem]" />
          <div className="pointer-events-none absolute bottom-[-12rem] left-[-10rem] size-[22rem] rounded-full bg-[#010d23]/55 opacity-80 blur-3xl sm:size-[30rem]" />
          <div className="relative mx-auto max-w-7xl px-4 text-center sm:px-5">
            <h2 className="mx-auto max-w-3xl text-[clamp(1.75rem,7vw,2.45rem)] font-bold leading-tight text-white sm:text-4xl lg:text-[2.75rem]">
              Your health history should travel with you.
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-base leading-relaxed text-white/78 sm:text-lg">
              Findable when it matters. Understandable when it counts.
            </p>
            <div className="mx-auto mt-6 grid w-full max-w-sm gap-3 sm:flex sm:max-w-none sm:items-center sm:justify-center">
              <a
                href="/app"
                className="inline-flex w-full items-center justify-center rounded-full bg-white px-7 py-3.5 text-base font-semibold text-[#084aa8] shadow-[0_22px_48px_-26px_rgba(0,0,0,0.62)] transition-all hover:-translate-y-0.5 hover:bg-[#f7fbff] hover:shadow-[0_28px_62px_-30px_rgba(0,0,0,0.68)] sm:w-auto"
              >
                Get started
              </a>
              <a
                href="/privacy"
                className="inline-flex w-full items-center justify-center rounded-full border border-white/24 bg-white/10 px-7 py-3.5 text-base font-semibold text-white backdrop-blur transition-all hover:-translate-y-0.5 hover:bg-white/16 sm:w-auto"
              >
                Privacy principles
              </a>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}

function LegalPage({
  title,
  intro,
  sections,
}: {
  title: string;
  intro: string;
  sections: Array<{ heading: string; body: string[] }>;
}) {
  return (
    <div className="min-h-screen">
      <SiteHeader />
      <main id="main">
        <article className="mx-auto max-w-3xl px-4 py-14 sm:px-5 sm:py-24">
          <p className="text-sm font-semibold uppercase tracking-widest text-primary">MedoPilot</p>
          <h1 className="mt-4 text-[clamp(2.25rem,11vw,3rem)] font-extrabold leading-tight">{title}</h1>
          <p className="mt-6 rounded-2xl border border-border bg-surface p-4 text-[0.975rem] leading-relaxed text-muted-foreground sm:p-5">
            {intro}
          </p>

          <div className="mt-12 space-y-10">
            {sections.map((section) => (
              <section key={section.heading} className="min-w-0">
                <h2 className="text-xl font-bold">{section.heading}</h2>
                {section.body.map((paragraph) => (
                  <p key={paragraph} className="mt-3 leading-relaxed text-muted-foreground">
                    {paragraph}
                  </p>
                ))}
              </section>
            ))}
          </div>
        </article>
      </main>
      <SiteFooter />
    </div>
  );
}

function PrivacyPage() {
  return (
    <LegalPage
      title="Privacy"
      intro="This page describes the privacy principles that govern how MedoPilot handles your health information. A full, legally reviewed privacy policy may be updated as the service evolves."
      sections={[
        {
          heading: "Our starting point",
          body: [
            "A medical history is among the most personal information a person has. MedoPilot is being designed on the principle that this information belongs to the individual, and that the product's role is to organize and protect it.",
          ],
        },
        {
          heading: "Information the product is designed to hold",
          body: [
            "MedoPilot is intended to store health information that you choose to add or upload, such as reports, prescriptions, consultation notes, medicines, allergies, vaccinations and treatment details, along with the basic account information needed to operate the service.",
            "You decide what goes into your record. You can choose to keep it minimal, or build a complete picture over time.",
          ],
        },
        {
          heading: "Your control",
          body: [
            "MedoPilot is designed around the principle that your health record should remain understandable to you. You decide what to add, update and keep organized over time.",
          ],
        },
        {
          heading: "Handling and security",
          body: [
            "Protecting health information is treated as a core product requirement. Security measures and operational practices should be reviewed continuously and described in specific terms in the full policy.",
            "We will not publish security or compliance claims we cannot substantiate.",
          ],
        },
        {
          heading: "What this page is not",
          body: [
            "This page does not constitute a jurisdiction-specific privacy policy, and it does not assert any regulatory certification, approval or compliance status.",
          ],
        },
      ]}
    />
  );
}

function TermsPage() {
  return (
    <LegalPage
      title="Terms"
      intro="This page sets out the terms guiding use of the MedoPilot product and this website. These terms may be updated as the service evolves."
      sections={[
        {
          heading: "What MedoPilot is",
          body: [
            "MedoPilot is a personal health-record and treatment companion. It is intended to help you organize medical records and documents, keep track of treatment prescribed by qualified healthcare professionals, and prepare your own health context before appointments or care decisions.",
          ],
        },
        {
          heading: "What MedoPilot is not",
          body: [
            "MedoPilot does not diagnose medical conditions, does not prescribe medicines, does not provide autonomous medical advice, and does not replace consultation with a qualified healthcare professional. It does not guarantee any medical outcome.",
            "Always seek the advice of a qualified healthcare professional regarding a medical condition or treatment. In an emergency, contact your local emergency services.",
          ],
        },
        {
          heading: "Your record and your responsibility",
          body: [
            "The accuracy and completeness of information stored in MedoPilot depends in part on what you add. Information organized within the product should be reviewed alongside original documents and professional guidance where clinical decisions are involved.",
          ],
        },
        {
          heading: "Accounts and sign-in",
          body: [
            "The MedoPilot Care Hub requires sign-in. You are responsible for keeping your sign-in details confidential and for activity carried out through your account.",
          ],
        },
        {
          heading: "Availability and changes",
          body: [
            "Features described publicly may change as the product improves, and availability may vary by region. These terms may be updated accordingly.",
          ],
        },
      ]}
    />
  );
}

function TransitionPage({ label }: { label?: string }) {
  return (
    <div className="min-h-screen">
      <SiteHeader />
      <main id="main">
        <BrandedLoader label={label} />
      </main>
      <SiteFooter />
    </div>
  );
}

function SignInRequired({ returnTo }: { returnTo: string }) {
  const auth = useAuth();
  const [isRedirecting, setIsRedirecting] = useState(auth.isConfigured);
  const [redirectError, setRedirectError] = useState<string | null>(null);

  const beginSignIn = async () => {
    if (auth.isSigningOut) {
      return;
    }

    setIsRedirecting(true);
    setRedirectError(null);

    try {
      await auth.signIn(returnTo);
    } catch {
      setIsRedirecting(false);
      setRedirectError("We could not start MedoPilot sign in. Please try again.");
    }
  };

  useEffect(() => {
    if (auth.status === "anonymous" && !auth.isSigningOut) {
      void beginSignIn();
    }
    // Run once when the protected page confirms there is no active session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.status, auth.isSigningOut]);

  if (auth.isConfigured && isRedirecting) {
    return <TransitionPage label="Please wait" />;
  }

  return (
    <div className="min-h-screen">
      <SiteHeader />
      <main id="main">
        <section className="mx-auto flex min-h-[calc(100svh-14rem)] max-w-3xl flex-col justify-center px-4 py-14 sm:px-5 sm:py-16">
          <p className="eyebrow mb-4">MedoPilot access</p>
          <h1 className="text-[clamp(2.25rem,11vw,2.75rem)] font-semibold leading-tight text-foreground">Sign in to continue</h1>
          <p className="mt-5 leading-8 text-muted-foreground">
            Access to MedoPilot is currently available by invitation. Please contact
            support@medopilot.com to request access.
          </p>
          {auth.isConfigured ? (
            <p className="mt-3 text-sm leading-6 text-muted-foreground">
              Use the button below if the sign-in page does not open automatically.
            </p>
          ) : null}
          {redirectError ? <p className="mt-4 text-sm font-medium text-destructive">{redirectError}</p> : null}
          {auth.error ? <p className="mt-4 text-sm font-medium text-destructive">{auth.error}</p> : null}
          <div className="mt-8">
            {auth.isConfigured ? (
              <button
                type="button"
                onClick={() => void beginSignIn()}
                disabled={isRedirecting}
                className="inline-flex w-full max-w-sm items-center justify-center gap-2 rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-[#3154d4] sm:w-auto"
              >
                Sign in
                <ArrowRight aria-hidden="true" size={18} />
              </button>
            ) : (
              <span className="inline-flex max-w-full rounded-full border border-border px-4 py-3 text-sm leading-snug text-muted-foreground">
                Invitation access is not available yet
              </span>
            )}
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}

function AuthCallbackPage() {
  const { completeSignIn } = useAuth();
  const [status, setStatus] = useState<"working" | "error">("working");

  useEffect(() => {
    let active = true;

    completeCallbackOnce(window.location.href, completeSignIn)
      .then((returnTo) => {
        if (active) {
          replaceLocation(returnTo);
        }
      })
      .catch(() => {
        if (active) {
          setStatus("error");
        }
      });

    return () => {
      active = false;
    };
  }, [completeSignIn]);

  return (
    <>
      {status === "working" ? (
        <TransitionPage />
      ) : (
        <div className="min-h-screen">
          <SiteHeader />
          <main id="main">
            <section className="mx-auto min-h-[calc(100svh-14rem)] max-w-3xl px-4 py-14 sm:px-5 sm:py-16">
              <p className="eyebrow mb-5">MedoPilot</p>
              <h1 className="text-[clamp(2.25rem,11vw,2.75rem)] font-semibold leading-tight text-foreground">
                Sign in needs another try
              </h1>
              <p className="mt-6 leading-8 text-muted-foreground">
                We could not complete your MedoPilot sign in. Please start again, or contact support@medopilot.com if
                the problem continues.
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <a
                  href="/app"
                  className="inline-flex justify-center rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground"
                >
                  Try again
                </a>
                <a
                  href="/"
                  className="inline-flex justify-center rounded-full border border-border px-5 py-3 text-sm font-semibold"
                >
                  Return home
                </a>
              </div>
            </section>
          </main>
          <SiteFooter />
        </div>
      )}
    </>
  );
}

function DeviceEmergencyLaunchGate({ apiBaseUrl, children }: { apiBaseUrl: string | null | undefined; children: ReactNode }) {
  const auth = useAuth();
  const location = useLocation();
  const bypassLaunchCheck = location.pathname === "/auth/callback";
  const [phase, setPhase] = useState<"checking" | "chooser" | "open" | "emergency-loading" | "emergency-ready" | "not-found" | "unavailable">(
    bypassLaunchCheck || !apiBaseUrl ? "open" : "checking",
  );
  const [card, setCard] = useState<EmergencyCardData | null>(null);

  useEffect(() => {
    if (bypassLaunchCheck || !apiBaseUrl) {
      setPhase("open");
      return undefined;
    }

    let active = true;
    const controller = new AbortController();
    setPhase("checking");
    setCard(null);

    getDeviceEmergencyAccessStatus(apiBaseUrl, controller.signal)
      .then((enabled) => {
        if (active) {
          setPhase(enabled ? "chooser" : "open");
        }
      })
      .catch(() => {
        if (active) {
          setPhase("open");
        }
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [apiBaseUrl, bypassLaunchCheck]);

  const openMedoPilot = () => setPhase("open");

  const openEmergency = () => {
    if (!apiBaseUrl) {
      setPhase("open");
      return;
    }

    setPhase("emergency-loading");
    setCard(null);
    fetchDeviceEmergencyCard(apiBaseUrl)
      .then((result) => {
        setCard(result);
        setPhase("emergency-ready");
      })
      .catch((error: unknown) => {
        if (error instanceof EmergencyCardNotFoundError) {
          setPhase("not-found");
          return;
        }
        setPhase(error instanceof EmergencyCardUnavailableError ? "unavailable" : "unavailable");
      });
  };

  if (phase === "open") {
    return <>{children}</>;
  }

  if (phase === "checking") {
    return <TransitionPage />;
  }

  if (auth.isAuthenticated) {
    return <>{children}</>;
  }

  if (auth.status === "loading") {
    return <TransitionPage label="Preparing your Care Hub" />;
  }

  if (phase === "emergency-loading") {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-[linear-gradient(180deg,#fff5f5,#fefefe_40%)] px-4">
        <BrandedLoader />
      </div>
    );
  }

  if (phase === "emergency-ready" && card) {
    return (
      <div className="flex min-h-dvh flex-col bg-[linear-gradient(180deg,#fff5f5,#fefefe_40%)] px-4 py-8 sm:py-12">
        <div className="mx-auto w-full max-w-md flex-1">
          <div className="mb-4 flex justify-end">
            <button type="button" onClick={openMedoPilot} className="rounded-full border border-border bg-white px-4 py-2 text-sm font-semibold text-foreground shadow-soft">
              Open MedoPilot
            </button>
          </div>
          <EmergencyCard card={card} variant="public" />
        </div>
      </div>
    );
  }

  if (phase === "not-found" || phase === "unavailable") {
    return (
      <DeviceEmergencyMessage
        title={phase === "not-found" ? "Emergency information is not available." : "Emergency information is temporarily unavailable."}
        onEmergency={openEmergency}
        onOpen={openMedoPilot}
      />
    );
  }

  return <DeviceEmergencyChooser onEmergency={openEmergency} onOpen={openMedoPilot} />;
}

function DeviceEmergencyChooser({ onEmergency, onOpen }: { onEmergency: () => void; onOpen: () => void }) {
  return (
    <div className="device-emergency-launch flex min-h-dvh items-center justify-center px-4 pb-[max(2rem,env(safe-area-inset-bottom))] pt-[max(2rem,env(safe-area-inset-top))] sm:px-6">
      <div className="device-emergency-arc left-[-10rem] top-[8%] size-[22rem] sm:left-[-6rem] sm:size-[30rem]" aria-hidden="true" />
      <div className="device-emergency-arc bottom-[-16rem] right-[-12rem] hidden size-[34rem] sm:block" aria-hidden="true" />
      <main id="main" className="w-full max-w-sm rounded-[1.5rem] border border-white/20 bg-white/95 p-5 text-center shadow-[0_32px_90px_-38px_rgba(0,0,0,0.78)] backdrop-blur-md sm:p-6">
        <div className="flex justify-center">
          <BrandLockup />
        </div>
        <div className="mt-7 grid gap-3">
          <button
            type="button"
            onClick={onEmergency}
            className="inline-flex min-h-14 items-center justify-center rounded-full bg-rose-600 px-5 text-base font-semibold text-white shadow-[0_20px_42px_-22px_rgba(225,29,72,0.95)] transition hover:bg-rose-500 focus:outline-none focus:ring-4 focus:ring-rose-500/25"
          >
            Emergency Info
          </button>
          <button
            type="button"
            onClick={onOpen}
            className="inline-flex min-h-14 items-center justify-center rounded-full border border-primary/25 bg-white px-5 text-base font-semibold text-primary transition hover:bg-primary/5 focus:outline-none focus:ring-4 focus:ring-primary/10"
          >
            Open MedoPilot
          </button>
        </div>
      </main>
    </div>
  );
}

function DeviceEmergencyMessage({ title, onEmergency, onOpen }: { title: string; onEmergency: () => void; onOpen: () => void }) {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-[linear-gradient(180deg,#fff5f5,#f8fbff)] px-4 py-8">
      <main id="main" className="w-full max-w-sm rounded-[1.5rem] border border-border bg-white p-5 text-center shadow-premium">
        <h1 className="text-lg font-bold text-foreground">{title}</h1>
        <div className="mt-5 grid gap-3">
          <button type="button" onClick={onEmergency} className="rounded-full bg-rose-600 px-5 py-3 text-sm font-semibold text-white">
            Try Emergency again
          </button>
          <button type="button" onClick={onOpen} className="rounded-full border border-primary/25 px-5 py-3 text-sm font-semibold text-primary">
            Open MedoPilot
          </button>
        </div>
      </main>
    </div>
  );
}

function ProtectedWorkspaceRoutes() {
  const auth = useAuth();
  const location = useLocation();
  const returnTo = `${location.pathname}${location.search}${location.hash}`;

  if (auth.status === "loading") {
    return <TransitionPage label="Preparing your Care Hub" />;
  }

  if (auth.isSigningOut) {
    return <TransitionPage label="Please wait" />;
  }

  if (!auth.isAuthenticated) {
    return <SignInRequired returnTo={returnTo} />;
  }

  return <WorkspaceShell />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      <Route path="/app" element={<ProtectedWorkspaceRoutes />}>
        <Route index element={<DashboardPage />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="upcoming-care" element={<UpcomingCarePage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="profile/edit" element={<ProfileEditPage />} />
        <Route path="profile/insurance" element={<HealthInsurancePage />} />
        <Route path="profile/export" element={<HealthProfileExportPage />} />
        <Route path="health-records" element={<HealthRecordsPage />} />
        <Route path="documents" element={<MyFilesPage />} />
        <Route path="documents/:documentId/view" element={<DocumentViewerPage />} />
        <Route path="health-documents" element={<Navigate to="/app/documents" replace />} />
        <Route path="allergies" element={<AllergiesPage />} />
        <Route path="allergies/new" element={<AllergyEditorPage />} />
        <Route path="allergies/:allergyId/edit" element={<AllergyEditorPage />} />
        <Route path="known-health-conditions" element={<KnownHealthConditionsPage />} />
        <Route path="known-health-conditions/new" element={<ConditionEditorPage />} />
        <Route path="known-health-conditions/:conditionId/edit" element={<ConditionEditorPage />} />
        <Route path="vaccinations" element={<VaccinationsPage />} />
        <Route path="vaccinations/new" element={<VaccinationNewPage />} />
        <Route path="vaccinations/:vaccinationId" element={<VaccinationDetailPage />} />
        <Route path="vaccinations/:vaccinationId/edit" element={<VaccinationEditPage />} />
        <Route path="appointments" element={<AppointmentsPage />} />
        <Route path="appointments/new" element={<AppointmentNewPage />} />
        <Route path="appointments/:appointmentId" element={<AppointmentDetailPage />} />
        <Route path="appointments/:appointmentId/edit" element={<AppointmentEditPage />} />
        <Route path="medications" element={<MedicationsPage />} />
        <Route path="medications/history" element={<MedicationsHistoryPage />} />
        <Route path="medications/new" element={<MedicationNewPage />} />
        <Route path="medications/:medicationId" element={<MedicationDetailPage />} />
        <Route path="medications/:medicationId/edit" element={<MedicationEditPage />} />
        <Route path="orders" element={<MedicineOrdersPage />} />
        <Route path="orders/new" element={<MedicineOrderNewPage />} />
        <Route path="orders/:orderId" element={<MedicineOrderDetailPage />} />
        <Route path="emergency" element={<EmergencyPage />} />
        <Route path="more" element={<MorePage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="settings/emergency-access" element={<EmergencyAccessPage />} />
        <Route path="account-security" element={<AccountSecurityPage />} />
        {/* Account & Security is now a top-level account destination; keep the old nested path working. */}
        <Route path="settings/security" element={<Navigate to="/app/account-security" replace />} />
        <Route path="*" element={<Navigate to="/app" replace />} />
      </Route>
      <Route path="*" element={<LandingPage />} />
    </Routes>
  );
}

function AppContent() {
  const { config, error, loading } = useRuntimeConfig();

  return (
    <>
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-6 focus:top-6 focus:z-50 focus:rounded-md focus:bg-primary focus:px-4 focus:py-2 focus:text-sm focus:text-primary-foreground"
      >
        Skip to content
      </a>
      {loading ? (
        <TransitionPage />
      ) : error ? (
        <div className="min-h-screen">
          <SiteHeader />
          <main id="main">
            <section className="mx-auto min-h-[calc(100svh-14rem)] max-w-3xl px-4 py-14 sm:px-5 sm:py-16">
              <p className="eyebrow mb-4">MedoPilot</p>
              <h1 className="text-[clamp(2.25rem,11vw,2.75rem)] font-semibold leading-tight text-foreground">
                Configuration unavailable
              </h1>
              <p className="mt-5 leading-8 text-muted-foreground">{error}</p>
            </section>
          </main>
          <SiteFooter />
        </div>
      ) : (
        <RuntimeConfigProvider config={config}>
          <AuthProvider config={config}>
            <DeviceEmergencyLaunchGate apiBaseUrl={config?.accountService?.apiBaseUrl}>
              <AppRoutes />
              <PwaStatusPrompts />
              <IosInstallInstructions />
              <AlreadyInstalledNotice />
              <NotificationEnablePrompt />
              <CareRemindersDialog />
            </DeviceEmergencyLaunchGate>
          </AuthProvider>
        </RuntimeConfigProvider>
      )}
    </>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}
